import {
  Component
} from '@angular/core';

import {
  MatDialogRef
} from '@angular/material/dialog';

import * as QRCode from 'qrcode';

import { jsPDF } from 'jspdf';

import {
  MessstelleQrInfo
} from '../../../Models/messstelle-qr.model';

import {
  MessstelleQrService
} from '../../../Services/messstelle-qr.service';


interface QrAnzeige {

  info: MessstelleQrInfo;

  dataUrl: string;
}


/*
 * ============================================================
 * ETIKETTEN-LAYOUT
 * ============================================================
 *
 * Bogen:     A4 (210 x 297 mm)
 * Etikett:   38 x 21,2 mm (Querformat), ohne Abstand zueinander
 * Raster:    5 Spalten x 13 Zeilen = 65 Etiketten pro Bogen
 *
 * Damit ergibt sich der Rand automatisch aus der Blattmitte:
 *   waagrecht: (210 - 5 * 38)   / 2 = 10,0  mm
 *   senkrecht: (297 - 13 * 21,2) / 2 = 10,7 mm
 */

const BOGEN_BREITE_MM = 210;
const BOGEN_HOEHE_MM = 297;

const ETIKETT_BREITE_MM = 38;
const ETIKETT_HOEHE_MM = 21.2;

const SPALTEN = 5;
const ZEILEN = 13;

export const ETIKETTEN_PRO_BOGEN =
  SPALTEN * ZEILEN;

const RAND_X_MM =
  (BOGEN_BREITE_MM - SPALTEN * ETIKETT_BREITE_MM) / 2;

const RAND_Y_MM =
  (BOGEN_HOEHE_MM - ZEILEN * ETIKETT_HOEHE_MM) / 2;

/* Der QR-Code selbst muss exakt 20 x 20 mm groß sein. */
const QR_MM = 20;

/* Linker Innenabstand des Etiketts bis zum QR-Code. */
const QR_LINKS_MM = 1;

const LOGO_PFAD = 'assets/logo.png';


@Component({
  selector: 'app-qr-code-admin',

  templateUrl:
    './qr-code-admin.component.html',

  styleUrls: [
    './qr-code-admin.component.css'
  ]
})
export class QrCodeAdminComponent {

  anzahl = 65;

  loading = false;

  erzeugtDokument = false;

  fehler = '';

  generiert: QrAnzeige[] = [];


  readonly etikettenProBogen =
    ETIKETTEN_PRO_BOGEN;


  constructor(
    private qrService:
      MessstelleQrService,

    private dialogRef:
      MatDialogRef<QrCodeAdminComponent>
  ) { }


  // ========================================================
  // ANZEIGE
  // ========================================================

  /** Bögen für die aktuell eingestellte Anzahl (Vorschau). */
  get bogenVorschau(): number {

    const anzahl =
      this.bereinigteAnzahl;

    if (anzahl < 1) {
      return 0;
    }

    return Math.ceil(
      anzahl / ETIKETTEN_PRO_BOGEN
    );
  }


  /** Bögen für die tatsächlich erzeugten Codes. */
  get bogenErzeugt(): number {

    if (this.generiert.length === 0) {
      return 0;
    }

    return Math.ceil(
      this.generiert.length / ETIKETTEN_PRO_BOGEN
    );
  }


  /**
   * Freie Etiketten auf dem letzten Bogen.
   * Damit sieht der Benutzer, ob ein Bogen nur teilweise
   * bedruckt wird.
   */
  get restEtiketten(): number {

    if (this.generiert.length === 0) {
      return 0;
    }

    const rest =
      this.generiert.length % ETIKETTEN_PRO_BOGEN;

    return rest === 0
      ? 0
      : ETIKETTEN_PRO_BOGEN - rest;
  }


  private get bereinigteAnzahl(): number {

    const wert =
      Math.floor(Number(this.anzahl));

    if (!Number.isFinite(wert)) {
      return 0;
    }

    return wert;
  }


  get eingabeGueltig(): boolean {

    const anzahl =
      this.bereinigteAnzahl;

    return anzahl >= 1
      && anzahl <= 650;
  }


  /** Setzt die Anzahl auf volle Etikettbögen. */
  aufVolleBogen(
    bogen: number
  ): void {

    this.anzahl =
      bogen * ETIKETTEN_PRO_BOGEN;
  }


  // ========================================================
  // GENERIEREN
  // ========================================================

  generieren(): void {

    if (
      this.loading
      ||
      !this.eingabeGueltig
    ) {
      return;
    }


    this.loading = true;

    this.fehler = '';

    this.generiert = [];


    this.qrService
      .generieren(
        this.bereinigteAnzahl
      )
      .subscribe({

        next: async codes => {

          try {

            this.generiert =
              await Promise.all(

                codes.map(
                  async info => {

                    const dataUrl =
                      await QRCode.toDataURL(
                        info.publicUrl,
                        {
                          /*
                           * 800 px auf 20 mm entspricht gut 1000 dpi.
                           * Damit bleibt der Ausdruck auch bei
                           * feinem Modulraster scharf.
                           */
                          width: 800,

                          /*
                           * Nur 1 Modul Ruhezone im Bild, damit der
                           * Code auf 20 mm möglichst groß wird.
                           * Die restliche Ruhezone liefert das
                           * weiße Etikett rundherum.
                           */
                          margin: 1,

                          errorCorrectionLevel: 'M'
                        }
                      );

                    return {
                      info,
                      dataUrl
                    };
                  }
                )

              );

          }
          catch (err) {

            console.error(
              'QR-Bilder konnten nicht erstellt werden:',
              err
            );

            this.fehler =
              'Die QR-Codes wurden gespeichert, konnten aber nicht '
              + 'als Etiketten aufbereitet werden.';
          }

          this.loading = false;
        },


        error: err => {

          console.error(
            'QR-Codes konnten nicht generiert werden:',
            err
          );

          this.fehler =
            err?.error?.message
            ??
            'Die QR-Codes konnten nicht erstellt werden.';

          this.loading = false;
        }

      });
  }


  // ========================================================
  // EXPORT / DRUCK
  // ========================================================

  async exportieren(): Promise<void> {

    const logoDataUrl =
      await this.ladeLogo();

    if (!logoDataUrl) {
      return;
    }

    const doc =
      this.baueDokument(logoDataUrl);

    if (!doc) {
      return;
    }

    const stempel =
      new Date()
        .toISOString()
        .slice(0, 10);

    doc.save(
      `QR-Etiketten_${stempel}_${this.generiert.length}-Stueck.pdf`
    );
  }


  async drucken(): Promise<void> {

    const logoDataUrl =
      await this.ladeLogo();

    if (!logoDataUrl) {
      return;
    }

    const doc =
      this.baueDokument(logoDataUrl);

    if (!doc) {
      return;
    }

    doc.autoPrint();

    const url =
      doc.output('bloburl');

    const fenster =
      window.open(
        url,
        '_blank'
      );

    if (!fenster) {

      this.fehler =
        'Das Druckfenster wurde vom Browser blockiert. '
        + 'Bitte Pop-ups erlauben oder die Etiketten exportieren.';
    }
  }

  private async ladeLogo(): Promise<string | null> {

    try {

      const response =
        await fetch(LOGO_PFAD);

      if (!response.ok) {
        throw new Error(
          `Logo konnte nicht geladen werden: ${response.status}`
        );
      }

      const blob =
        await response.blob();

      return await new Promise<string>(
        (resolve, reject) => {

          const reader =
            new FileReader();

          reader.onload = () =>
            resolve(reader.result as string);

          reader.onerror = () =>
            reject(reader.error);

          reader.readAsDataURL(blob);
        }
      );

    }
    catch (err) {

      console.error(
        'ELIN-Logo konnte nicht geladen werden:',
        err
      );

      this.fehler =
        'Das ELIN-Logo konnte nicht geladen werden.';

      return null;
    }
  }

  /**
   * Baut den Etikettbogen millimetergenau auf.
   *
   * Es wird bewusst kein HTML gedruckt: Browser skalieren
   * Seiteninhalte je nach Druckeinstellung, wodurch die 20 mm
   * des QR-Codes nicht mehr stimmen würden.
   */
  private baueDokument(
    logoDataUrl: string
  ): jsPDF | null {

    if (this.generiert.length === 0) {

      this.fehler =
        'Es wurden noch keine QR-Codes erzeugt.';

      return null;
    }


    this.fehler = '';


    const doc =
      new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });


    this.generiert.forEach(
      (qr, index) => {

        const positionAufBogen =
          index % ETIKETTEN_PRO_BOGEN;

        if (
          index > 0
          &&
          positionAufBogen === 0
        ) {

          doc.addPage(
            'a4',
            'portrait'
          );


        }


        const spalte =
          positionAufBogen % SPALTEN;

        const zeile =
          Math.floor(
            positionAufBogen / SPALTEN
          );


        const x =
          RAND_X_MM + spalte * ETIKETT_BREITE_MM;

        const y =
          RAND_Y_MM + zeile * ETIKETT_HOEHE_MM;


        /* QR-Code: exakt 20 x 20 mm, senkrecht zentriert. */
        doc.addImage(
          qr.dataUrl,
          'PNG',
          x + QR_LINKS_MM,
          y + (ETIKETT_HOEHE_MM - QR_MM) / 2,
          QR_MM,
          QR_MM,
          undefined,
          'FAST'
        );


        /*
 * ELIN-Logo rechts neben dem QR-Code.
 * Das Logo wird um 90° gedreht und in der freien
 * Fläche neben dem QR-Code zentriert.
 */

        const logoOriginalBreite = 958;
        const logoOriginalHoehe = 264;

        const logoVerhaeltnis =
          logoOriginalBreite / logoOriginalHoehe;


        /* Größe des Logos VOR der Drehung */
        const logoBreite = 16;

        const logoHoehe =
          logoBreite / logoVerhaeltnis;


        /*
         * Mittelpunkt der freien Fläche rechts vom QR-Code.
         */
        const restStartX =
          x + QR_LINKS_MM + QR_MM;

        const restBreite =
          ETIKETT_BREITE_MM
          - QR_LINKS_MM
          - QR_MM;

        const mitteX =
          restStartX + restBreite / 2;

        const mitteY =
          y + ETIKETT_HOEHE_MM / 2;


        /*
         * Bei 90°-Drehung:
         *
         * Die ursprüngliche Breite wird vertikal,
         * die ursprüngliche Höhe horizontal.
         *
         * Der Einfügepunkt muss deshalb entsprechend
         * vom Mittelpunkt aus versetzt werden.
         */
        const logoX =
          mitteX - logoHoehe / 2;

        const logoY =
          mitteY + logoBreite / 2 - 4;


        doc.addImage(
          logoDataUrl,
          'PNG',
          logoX,
          logoY,
          logoBreite,
          logoHoehe,
          undefined,
          'FAST',
          90
        );

      }
    );


    this.erzeugtDokument = true;

    return doc;
  }





  schliessen(): void {

    this.dialogRef.close(
      this.generiert.length
    );
  }
}
